import React, {
  ReactNode,
  ReactElement,
  useState,
  useRef,
  MouseEvent,
  useCallback,
  useEffect,
} from 'react';
import styled from 'styled-components';
import DropdownButton from './DropdownButton';
import DropdownMenu from './DropdownMenu';
import useOnClickOutside from 'use-onclickoutside';
import { StyledButton } from 'ui/components';
import { confirmAlert } from 'utils/confirm-alert';

const StyledDropdown = styled.div`
  position: relative;
  display: inline-block;

  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.colors.gray[1]};
  }

  /* Column selection dropdown */
  &.dd-col-selection {
    summary {
      background: none;
      border: none;
      padding: ${({ theme }) => theme.space[2]} ${({ theme }) => theme.space[1]};
      line-height: 1.2;
      box-shadow: none;
      display: flex;
      align-items: center;

      &:focus,
      &:active {
        outline: none;
        background: ${({ theme }) => theme.colors.bg.grayDark};
        color: #fff;
      }
    }
  }

  /* List item options dropdown */
  &.dd-listitem-options {
    summary {
      padding: 0;

      button {
        padding: ${({ theme }) => theme.space[1]};
      }
    }
  }

  &.dd-procedure-filter {
    summary {
      padding: 0;

      & + * {
        width: 280px;
        max-width: 280px;
        padding: 0 !important;
      }

      button {
        padding: ${({ theme }) => theme.space[1]};
      }
    }
  }
`;

export type DropdownProps = {
  trigger?: string | ReactNode;
  menuList?: Array<item>;
  menuContent?: ReactNode;
  className?: any;
  autoclose?: boolean;
  position?: 'right-top' | 'right' | 'left-top' | 'left';
  ref?: React.RefObject<HTMLDivElement>;
  onClick?: (e: any) => void;
  onButtonClick?: (categoryId?: number | null) => void;
  children?: ReactNode;
  categoryMenu?: boolean;
  disabled?: boolean;
};

type item = {
  id?: number;
  value: string;
  action?: () => void;
  icon?: ReactNode;
  groupEnd?: boolean;
  colorCode?: string;
  pattern?: string;
};

function Dropdown({
  trigger,
  menuList,
  menuContent,
  className,
  autoclose = true,
  position,
  ref,
  onClick,
  onButtonClick,
  disabled,
  children,
  categoryMenu,
}: DropdownProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const handleOnClickOutside = () => {
    setOpen(false);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(wrapperRef, handleOnClickOutside);

  // toggle only from the trigger
  const handleToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // keep it from bubbling upward
    setOpen(prev => (autoclose === true ? !prev : true));
    if (onClick) onClick(e);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (open && e.key === 'Escape') setOpen(false);
    },
    [open],
  );

  const focusShift = useRef<HTMLUListElement>(null);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!open) return;
 // Detect the click inside the tinymce iframe & trigger the close action of this component.
    const iframe = document.querySelector(
      'iframe.tox-edit-area__iframe',
    ) as HTMLIFrameElement | null;
    if (!iframe) return;

    let iframeDoc: Document | null = null;

    try {
      iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document || null;
    } catch (err) {
      console.warn('Cross-origin iframe detected, skipping', err);
      return;
    }

    if (!iframeDoc) return;

    const handleIframeClick = () => {
      handleOnClickOutside();
    };

    iframeDoc.addEventListener('mousedown', handleIframeClick);

    return () => {
      if (iframeDoc !== null) {
        iframeDoc.removeEventListener('mousedown', handleIframeClick);
      }
    };
  }, [open]);

  return (
    <StyledDropdown ref={wrapperRef} className={className}>
      {/* Pass the toggle to the trigger button only */}
      <DropdownButton onClick={handleToggle} disabled={disabled}>
        {trigger}
      </DropdownButton>

      {open && (
        <DropdownMenu
          position={position}
          width={categoryMenu ? '300px' : undefined}
        >
          {categoryMenu && (
            <p
              style={{
                paddingLeft: '10px',
                paddingBottom: '8px',
                paddingTop: '8px',
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              Categories
            </p>
          )}

          {menuList ? (
            <ul
              data-testid="header-profile-list"
              ref={focusShift}
              className="dropdownMenuList"
              tabIndex={0}
            >
              {menuList.map((item, index) => {
                const itemId = item.id || index;
                const isAlreadySelected = selectedCategoryId === itemId;
                return (
                  <li
                    key={index}
                    style={{ marginInline: categoryMenu ? '8px' : undefined }}
                  >
                    <button
                      data-testid={`button${item.id}`}
                      type="button"
                      onClick={e => {
                        // Prevent click from bubbling (so wrapper handlers won't run)
                        e.stopPropagation();

                        // For categoryMenu, do NOT close the dropdown on item click
                        if (!categoryMenu) {
                          setOpen(false);
                        } else {
                          // Track the selected category
                          if (isAlreadySelected) {
                            setSelectedCategoryId(null);
                            focusShift.current?.focus();
                          } else {
                            setSelectedCategoryId(itemId);
                          }
                        }
                        // run item action
                        item.action && item.action();
                      }}
                      style={{
                        padding: categoryMenu ? '7px' : undefined,
                        gap: categoryMenu ? '8px' : undefined,
                      }}
                      className={isAlreadySelected ? 'selected' : ''}
                    >
                      {item.icon ? item.icon : null}
                      {categoryMenu && (
                        <div
                          style={{ display: 'flex', alignItems: 'center' }}
                          dangerouslySetInnerHTML={{
                            __html: `<span class="pcs-procedure-category__pattern_alias" style="--category-base: ${
                              item?.colorCode ?? '#fff'
                            }">
                            ${
                              item?.pattern
                                ? item?.pattern?.replace(
                                    /pattern-([a-z-]+)/g,
                                    `pattern-$1-${item.id}`,
                                  )
                                : ''
                            }
                          </span>`,
                          }}
                        />
                      )}
                      <span className="pr-button-label">{item.value}</span>
                    </button>

                    {item.groupEnd ? (
                      <div className="dd-divider" aria-hidden="true" />
                    ) : null}
                  </li>
                );
              })}

              {categoryMenu && (
                <div
                  style={{
                    marginTop: '5px',
                    borderTop: `1px solid var(--color-borer-default)`,
                    width: '100%',
                    paddingInline: '5px',
                    paddingBlockStart: '5px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <StyledButton
                    variant="primary"
                    // size="sm"
                    fullWidth={true}
                    disabled={!selectedCategoryId}
                    onClick={e => {
                      e.stopPropagation();
                      confirmAlert({
                        message:
                          'Are you sure you want to change the procedure category?',
                        buttons: [
                          {
                            label: 'Yes',
                            className: 'pr-btn-primary',
                            onClick: () => {
                              onButtonClick &&
                                onButtonClick(selectedCategoryId);
                              setOpen(false);
                              setSelectedCategoryId(null);
                            },
                          },
                          {
                            label: 'No',
                            onClick: () => {
                              setOpen(true);
                            },
                          },
                        ],
                      });
                      // If EnableButton needs to do something else, do it here
                    }}
                  >
                    Update Category
                  </StyledButton>
                </div>
              )}
            </ul>
          ) : (
            null || menuContent || children
          )}
        </DropdownMenu>
      )}
    </StyledDropdown>
  );
}

export { Dropdown, DropdownButton, DropdownMenu, StyledDropdown };
